import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { FilterButton, InfinityLoader, ListItemBox, LoadingCards } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { activateModal } from "domains/modal/actions";
import { applyPublicationSearchCriteria } from "domains/publication/actions";
import { ExpandableKnowledgeItem } from "domains/publication/components/ExpandableKnowledgeItem";
import {
  createPublicationsForTopicSelector,
  getIsPublicationFilterCount,
  getIsPublicationFiltered,
  getPublicationSearchTerm,
} from "domains/publication/selectors";
import { TopicDetailSection } from "domains/topicDetail/components/TopicDetailSection";
import { TopicSearchBar } from "domains/topicSearchFilter/components/TopicSearchBar";
import { getContentHasFilters } from "domains/topicSearchFilter/selectors";
import { getTmSelectedId, getTmSelectedOuterNode } from "domains/transformationMap/selectors";
import { createKnowledgesSelector } from "entities/knowledge/selectors";
import { getCanAccessTopicPublications, getCanSeeSectionContent } from "entities/permissions/selectors";
import { createHasUnauthenticatedContentAccessSelector } from "entities/topics/selectors";

const StyledRow = styled.div`
  display: flex;
`;

const StyledFilterSearchRow = styled(StyledRow)`
  margin-bottom: 1rem;
`;

const StyledSearch = styled(TopicSearchBar)`
  margin: 0;
`;

const StyledCard = styled(ListItemBox)`
  padding: 1rem;
`;

type PublicationSectionProps = {
  selectedSectionId?: string;
  selectSectionHandle: (id: string) => void;
  topicId: string;
};

const SECTION_ID = "publications";
const ITEM_LIMIT = 4;

export const PublicationSection = ({ topicId, selectedSectionId, selectSectionHandle }: PublicationSectionProps) => {
  const isExpanded = SECTION_ID === selectedSectionId;

  const dispatch = useDispatch();
  const location = useLocation();
  const unauthenticatedSectionAccess = useSelector(createHasUnauthenticatedContentAccessSelector(topicId));
  const hasAccess = useSelector(getCanAccessTopicPublications);
  const canSeeSection = useSelector(getCanSeeSectionContent);
  const hasFilters = useSelector(getContentHasFilters("publications"));
  const isFiltered = useSelector(getIsPublicationFiltered);
  const filterCount = useSelector(getIsPublicationFilterCount);
  const searchTerm = useSelector(getPublicationSearchTerm);
  const selectedInnerNodeId = useSelector(getTmSelectedId);
  const selectedOuterNode = useSelector(getTmSelectedOuterNode);
  const translate = useTranslate();

  const id = selectedInnerNodeId ?? topicId;

  const publicationsForTopic = useSelector(createPublicationsForTopicSelector(id));

  const knowledgesSelector = useMemo(
    () => createKnowledgesSelector(publicationsForTopic?.data ?? []),
    [publicationsForTopic?.data],
  );

  const knowledge = useSelector(knowledgesSelector).slice(0, isExpanded ? undefined : ITEM_LIMIT);

  const hasNextPage = publicationsForTopic?.hasNextPage ?? false;
  const isPending = publicationsForTopic?.isFetching ?? false;

  useEffect(() => {
    if (id && knowledge.length === 0) {
      dispatch(applyPublicationSearchCriteria());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id && selectedOuterNode?.id) {
      dispatch(applyPublicationSearchCriteria());
    }
  }, [dispatch, id, selectedOuterNode?.id]);

  const isLocked = !unauthenticatedSectionAccess && hasAccess && !canSeeSection;

  const hide = !canSeeSection && !hasAccess;

  const path = `${location.pathname}/publications`;

  const loadNextPage = () => {
    dispatch(
      applyPublicationSearchCriteria({
        offset: knowledge.length,
        limit: 20,
      }),
    );
  };

  const onFilterClick = () => dispatch(activateModal({ id: "TopicPublicationFilter", params: { topicId: id } }));

  const onSearchHandle = (search: string) => dispatch(applyPublicationSearchCriteria({ search }));

  const onSelection = () => selectSectionHandle(SECTION_ID);

  const isHidden = selectedSectionId !== SECTION_ID && !!selectedSectionId;

  if (knowledge.length === 0 && !isPending && !isFiltered) {
    return null;
  }

  return (
    <TopicDetailSection
      title={translate("app.discover.topic.tabs.knowledge.title")}
      display={!hide}
      isExpanded={isExpanded}
      isHidden={isHidden}
      selectSectionHandle={onSelection}
      isLocked={isLocked}
      isPending={isPending}
      filterCount={filterCount}
      hasNextPage={knowledge.length >= ITEM_LIMIT}
      topicId={topicId}
    >
      {isExpanded && (
        <StyledFilterSearchRow>
          <StyledSearch onSearch={onSearchHandle} isPending={isPending} defaultValue={searchTerm} />
          {hasFilters && <FilterButton count={filterCount} onClick={onFilterClick} />}
        </StyledFilterSearchRow>
      )}
      <InfinityLoader loadNextPage={loadNextPage} hasNextPage={isExpanded && hasNextPage} isPending={isPending}>
        {knowledge.map((article) => (
          <ListItemBox key={article.id}>
            <ExpandableKnowledgeItem
              id={article.id}
              title={article.title}
              summary={article.summary}
              source={article.source}
              timestamp={article.time}
              signals={article.signals ?? []}
              isBookmarked={!!article.is_bookmarked}
              isPinned={!!article.is_pinned}
              externalLink={article.link}
              detailLink={`${path}/${article.id}`}
            />
          </ListItemBox>
        ))}
        {isPending && knowledge.length === 0 && !isHidden && (
          <LoadingCards
            cardHeight={66}
            displayDetailButton
            imageHeight={50}
            imageWidth={50}
            lines={[30, 100]}
            numberOfItems={ITEM_LIMIT}
          />
        )}
      </InfinityLoader>
      {knowledge.length === 0 && !isPending && isFiltered && (
        <StyledCard>{translate("app.monitor.empty_message_filter")}</StyledCard>
      )}
    </TopicDetailSection>
  );
};
